import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Archive,
  ArchiveRestore,
  Paperclip,
  Download,
  X,
  MapPin,
  Tag,
  Clock,
  User,
  Upload,
  FileText,
  Loader2,
  Mail,
  ChevronDown,
  ChevronUp,
  Sparkles,
  CheckCircle2,
  CalendarClock,
  FileText as FileTextIcon,
  Eye,
  Lock,
  ShieldAlert,
  Users
} from 'lucide-react';
import { VisibilityBadge } from '../components/VisibilitySelector';
import KeyDataSection from '../components/KeyDataSection';

const CATEGORIES = {
  financieel: 'Financieel & Belasting',
  contracten: 'Contracten & Huur',
  leveranciers: 'Leveranciers & Inkoop',
  techniek: 'Techniek & Apparatuur',
  voertuigen: 'Voertuigen',
  vastgoed: 'Vastgoed & Facilitair',
  veiligheid: 'Veiligheid & Compliance',
  milieu: 'Milieu & Infrastructuur',
  nieuwbouw: 'Nieuwbouw & Verbouwing',
  overnames: 'Overnames',
  automatisering: 'Automatisering',
};

const COMPLIANCE_TYPES = [
  { id: 'keuring', name: 'Keuring' },
  { id: 'contract', name: 'Contract' },
  { id: 'onderhoud', name: 'Onderhoud' },
  { id: 'voertuig', name: 'Voertuig' },
  { id: 'vergunning', name: 'Vergunning' },
  { id: 'betaling', name: 'Betaling' },
];

const RECURRENCE_OPTIONS = [
  { id: 'one-time', name: 'Eenmalig' },
  { id: 'monthly', name: 'Maandelijks' },
  { id: 'quarterly', name: 'Per kwartaal' },
  { id: 'half-yearly', name: 'Halfjaarlijks' },
  { id: 'yearly', name: 'Jaarlijks' },
];

const STATUS_CLASSES = { active: 'badge-active', pending: 'badge-pending', pending_review: 'badge-pending', archived: 'badge-archived' };
const STATUS_LABELS = { active: 'Actief', pending: 'Concept', pending_review: 'Te Beoordelen', archived: 'Gearchiveerd' };

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function canPreview(mime, filename) {
  if (mime === 'application/pdf' || mime?.startsWith('image/')) return true;
  if (filename?.toLowerCase().endsWith('.pdf')) return true;
  const imgExts = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
  if (filename && imgExts.some(ext => filename.toLowerCase().endsWith(ext))) return true;
  return false;
}

function AttachmentRow({ att, authAxios, onDelete, previewAtt, setPreviewAtt }) {
  const isActive = previewAtt?.file_id === att.file_id;

  const handleDownload = () => {
    const api = authAxios();
    api.get(`/attachments/${att.file_id}`, { responseType: 'blob' })
      .then(res => {
        const blobUrl = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = att.original_name;
        a.click();
        URL.revokeObjectURL(blobUrl);
      });
  };

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive ? 'bg-[#2E75B6]/5 ring-1 ring-[#2E75B6]/20' : 'hover:bg-slate-50'}`}>
      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-700 truncate">{att.original_name}</p>
        <p className="text-[10px] text-slate-400">{formatFileSize(att.size)}</p>
      </div>
      {canPreview(att.mime_type, att.original_name) && (
        <button
          onClick={() => setPreviewAtt(isActive ? null : att)}
          className={`p-1.5 rounded-md transition-colors ${isActive ? 'text-[#2E75B6] bg-[#2E75B6]/10' : 'text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5'}`}
          title="Bekijk"
          data-testid={`preview-attachment-${att.file_id}`}
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        onClick={handleDownload}
        className="p-1.5 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
        title="Download"
        data-testid={`download-attachment-${att.file_id}`}
      >
        <Download className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => onDelete(att.file_id)}
        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        title="Verwijderen"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function AttachmentPreview({ att, authAxios, onClose }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const api = authAxios();
    api.get(`/attachments/${att.file_id}`, { responseType: 'blob' })
      .then(res => {
        if (!cancelled) {
          const url = URL.createObjectURL(res.data);
          setBlobUrl(url);
        }
      })
      .catch(() => { if (!cancelled) setBlobUrl(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att.file_id]);

  const isPdf = att.mime_type === 'application/pdf' || att.original_name?.toLowerCase().endsWith('.pdf');
  const isImage = att.mime_type?.startsWith('image/') || ['.jpg','.jpeg','.png','.webp','.heic','.heif'].some(ext => att.original_name?.toLowerCase().endsWith(ext));

  return (
    <div className="mt-3 rounded-xl border border-slate-200 overflow-hidden bg-slate-50" data-testid="attachment-preview-panel">
      <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-slate-100">
        <span className="text-xs font-medium text-slate-600 truncate">{att.original_name}</span>
        <button onClick={onClose} className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid="close-preview">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : blobUrl ? (
        isPdf ? (
          <iframe src={blobUrl} className="w-full h-[500px] border-0" title={att.original_name} data-testid="pdf-preview-iframe" />
        ) : isImage ? (
          <div className="p-4 flex justify-center bg-white">
            <img src={blobUrl} alt={att.original_name} className="max-h-[500px] max-w-full object-contain rounded-lg" data-testid="image-preview" />
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-slate-500">Preview niet beschikbaar — download om te bekijken</div>
        )
      ) : (
        <div className="p-6 text-center text-sm text-slate-500">Preview niet beschikbaar — download om te bekijken</div>
      )}
    </div>
  );
}

export default function EntryDetail() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const { authAxios, user } = useAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailSectionOpen, setEmailSectionOpen] = useState(true);
  const [accepting, setAccepting] = useState(false);
  // Compliance auto-creation from email review
  const [createCompliance, setCreateCompliance] = useState(false);
  const [compTitle, setCompTitle] = useState('');
  const [compType, setCompType] = useState('');
  const [compDueDate, setCompDueDate] = useState('');
  const [compRecurrence, setCompRecurrence] = useState('one-time');
  const [previewAtt, setPreviewAtt] = useState(null);

  const loadEntry = async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/entries/${entryId}`);
      setEntry(res.data);
      // Pre-fill compliance from extracted_data
      const compSuggestion = res.data?.extracted_data?.suggested_compliance_item;
      if (compSuggestion && res.data?.entry_status === 'pending_review') {
        setCreateCompliance(true);
        setCompTitle(compSuggestion.title || '');
        setCompType(compSuggestion.type || '');
        setCompDueDate(compSuggestion.due_date || '');
        setCompRecurrence(compSuggestion.recurrence || 'one-time');
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Item niet gevonden');
        navigate('/kennisbank');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntry();
  }, [entryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleArchiveToggle = async () => {
    try {
      const api = authAxios();
      const newStatus = entry.entry_status === 'archived' ? 'active' : 'archived';
      await api.put(`/entries/${entryId}`, { entry_status: newStatus });
      setEntry({ ...entry, entry_status: newStatus });
      toast.success(newStatus === 'archived' ? 'Item gearchiveerd' : 'Item hersteld');
    } catch (e) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Weet je zeker dat je dit item wilt verwijderen?')) return;
    setDeleting(true);
    try {
      const api = authAxios();
      await api.delete(`/entries/${entryId}`);
      toast.success('Item verwijderd');
      navigate('/kennisbank');
    } catch (e) {
      toast.error('Fout bij verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const api = authAxios();
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/entries/${entryId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Bijlage(n) ge\u00fcpload');
      await loadEntry();
    } catch (e) {
      toast.error('Fout bij uploaden');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (fileId) => {
    try {
      const api = authAxios();
      await api.delete(`/entries/${entryId}/attachments/${fileId}`);
      toast.success('Bijlage verwijderd');
      await loadEntry();
    } catch (e) {
      toast.error('Fout bij verwijderen bijlage');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const handleAcceptAndActivate = async () => {
    setAccepting(true);
    try {
      const api = authAxios();
      const updates = { entry_status: 'active' };
      if (entry.ai_suggestions) {
        if (entry.ai_suggestions.category) updates.category = entry.ai_suggestions.category;
        if (entry.ai_suggestions.location) updates.location_id = entry.ai_suggestions.location;
        if (entry.ai_suggestions.tags?.length) updates.tags = entry.ai_suggestions.tags.join(', ');
        updates.ai_categorized = true;
      }
      await api.put(`/entries/${entryId}`, updates);

      // Create compliance item if requested
      if (createCompliance && compTitle && compType && compDueDate) {
        try {
          await api.post('/compliance-items', {
            location_id: entry.location_id || 'all',
            type: compType,
            title: compTitle,
            description: `Aangemaakt vanuit e-mail: ${entry.source_subject || entry.title}`,
            due_date: compDueDate,
            recurrence: { interval: compRecurrence, months: 0 },
            reminder_days: [60, 30, 7],
            link_to_vault_entry: entryId,
          });
          toast.success('Item goedgekeurd, geactiveerd en compliance item aangemaakt');
        } catch (compErr) {
          console.error('Compliance creation failed:', compErr);
          toast.success('Item goedgekeurd en geactiveerd');
          toast.error('Compliance item aanmaken mislukt');
        }
      } else {
        toast.success('Item goedgekeurd en geactiveerd');
      }
      await loadEntry();
    } catch (e) {
      toast.error('Fout bij goedkeuren');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1000px] space-y-4">
        <div className="h-8 w-32 bg-slate-100 rounded animate-pulse" />
        <div className="bg-white rounded-xl border border-slate-200/80 p-6 animate-pulse">
          <div className="h-6 w-3/4 bg-slate-100 rounded mb-4" />
          <div className="h-4 w-full bg-slate-100 rounded mb-2" />
          <div className="h-4 w-2/3 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  if (!entry && !accessDenied) return null;

  if (accessDenied) {
    return (
      <div className="max-w-[600px] mx-auto mt-12">
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-red-200 dark:border-red-900/30 p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-900 dark:text-[#E6EDF3] mb-2">Geen toegang</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Dit item is vertrouwelijk en je hebt geen toegang. Neem contact op met de eigenaar als je toegang nodig hebt.
          </p>
          <button
            onClick={() => navigate('/kennisbank')}
            data-testid="access-denied-back"
            className="px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] transition-colors"
          >
            Terug naar Kennisbank
          </button>
        </div>
      </div>
    );
  }

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="max-w-[1000px]">
      {/* Back button */}
      <button
        onClick={() => navigate('/kennisbank')}
        data-testid="back-to-kennisbank"
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#2E75B6] mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Terug naar Kennisbank
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Title + Actions */}
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 mb-4">
              <h1 className="font-heading text-lg sm:text-xl font-bold text-slate-900 dark:text-[#E6EDF3]" data-testid="entry-title">
                {entry.title}
              </h1>
              <VisibilityBadge visibility={entry.visibility} />
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => {
                    const api = authAxios();
                    api.get(`/export/entry/${entryId}/pdf`, { responseType: 'blob' })
                      .then(res => {
                        const blobUrl = URL.createObjectURL(res.data);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = `tw1-${(entry.title || 'entry').slice(0, 40).replace(/ /g, '_')}.pdf`;
                        a.click();
                        URL.revokeObjectURL(blobUrl);
                      })
                      .catch(() => { toast.error('PDF export mislukt'); });
                  }}
                  data-testid="entry-download-pdf-button"
                  className="p-2 rounded-lg text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors duration-200"
                  title="Download PDF"
                >
                  <FileText className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate(`/kennisbank/${entryId}/bewerken`)}
                  data-testid="entry-edit-button"
                  className="p-2 rounded-lg text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors duration-200"
                  title="Bewerken"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleArchiveToggle}
                  data-testid="entry-archive-button"
                  className="p-2 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors duration-200"
                  title={entry.entry_status === 'archived' ? 'Herstellen' : 'Archiveren'}
                >
                  {entry.entry_status === 'archived' ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  data-testid="entry-delete-button"
                  className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-200"
                  title="Verwijderen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div
              className="prose prose-sm max-w-none text-slate-700"
              data-testid="entry-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.content || '<p class="text-slate-400 italic">Geen inhoud</p>') }}
            />
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-xl border border-slate-200/80 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Bijlagen ({entry.attachments?.length || 0})
              </h3>
              <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 text-xs text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? 'Uploaden...' : 'Upload'}
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="attachment-upload-input"
                />
              </label>
            </div>

            {(!entry.attachments || entry.attachments.length === 0) ? (
              <p className="text-xs text-slate-400 text-center py-4">Geen bijlagen</p>
            ) : (
              <div className="space-y-2">
                {entry.attachments.map(att => (
                  <AttachmentRow key={att.file_id} att={att} authAxios={authAxios} onDelete={handleDeleteAttachment} previewAtt={previewAtt} setPreviewAtt={setPreviewAtt} />
                ))}
              </div>
            )}

            {/* Inline preview panel */}
            {previewAtt && (
              <AttachmentPreview att={previewAtt} authAxios={authAxios} onClose={() => setPreviewAtt(null)} />
            )}
          </div>
        </div>

        {/* Metadata sidebar */}
        <div className="space-y-4">
          {/* Review Banner for pending_review entries */}
          {entry.entry_status === 'pending_review' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="review-banner">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">Te Beoordelen</span>
              </div>
              <p className="text-xs text-amber-700">Dit item is via e-mail binnengekomen en wacht op goedkeuring.</p>

              {entry.ai_suggestions && entry.ai_suggestions.category && (
                <div className="space-y-2 pt-2 border-t border-amber-200">
                  <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">AI Suggesties</p>
                  <div className="flex flex-wrap gap-1.5">
                    <span data-testid="ai-suggestion-category" className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#2E75B6]/10 text-[#2E75B6]">
                      <Sparkles className="w-2.5 h-2.5" />
                      {CATEGORIES[entry.ai_suggestions.category] || entry.ai_suggestions.category}
                    </span>
                    {entry.ai_suggestions.location && (
                      <span data-testid="ai-suggestion-location" className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        <MapPin className="w-2.5 h-2.5" />
                        {entry.ai_suggestions.location}
                      </span>
                    )}
                    {entry.ai_suggestions.tags?.map((tag, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Compliance auto-creation */}
              {entry.extracted_data?.suggested_compliance_item && (
                <div className="space-y-2 pt-2 border-t border-amber-200" data-testid="email-compliance-section">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createCompliance}
                      onChange={(e) => setCreateCompliance(e.target.checked)}
                      data-testid="email-compliance-toggle"
                      className="w-3.5 h-3.5 rounded border-amber-300 text-[#2E75B6] focus:ring-[#2E75B6]/30"
                    />
                    <span className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5" />
                      Compliance item aanmaken
                    </span>
                  </label>
                  {createCompliance && (
                    <div className="space-y-2 pl-5.5">
                      <input
                        type="text"
                        value={compTitle}
                        onChange={(e) => setCompTitle(e.target.value)}
                        data-testid="email-compliance-title"
                        className="w-full px-2 py-1.5 rounded-md border border-amber-200 bg-white text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/30"
                        placeholder="Compliance titel"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={compType}
                          onChange={(e) => setCompType(e.target.value)}
                          data-testid="email-compliance-type"
                          className="px-2 py-1.5 rounded-md border border-amber-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/30"
                        >
                          <option value="">Type</option>
                          {COMPLIANCE_TYPES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        <input
                          type="date"
                          value={compDueDate}
                          onChange={(e) => setCompDueDate(e.target.value)}
                          data-testid="email-compliance-due-date"
                          className="px-2 py-1.5 rounded-md border border-amber-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/30"
                        />
                      </div>
                      <select
                        value={compRecurrence}
                        onChange={(e) => setCompRecurrence(e.target.value)}
                        data-testid="email-compliance-recurrence"
                        className="w-full px-2 py-1.5 rounded-md border border-amber-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/30"
                      >
                        {RECURRENCE_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleAcceptAndActivate}
                  disabled={accepting}
                  data-testid="accept-activate-button"
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  {accepting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Goedkeuren & Activeren
                </button>
                <button
                  onClick={() => navigate(`/kennisbank/${entryId}/bewerken`)}
                  data-testid="review-edit-button"
                  className="px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Bewerken
                </button>
              </div>
            </div>
          )}

          {/* Email Source Metadata */}
          {entry.source === 'email' && (
            <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="email-source-section">
              <button
                onClick={() => setEmailSectionOpen(!emailSectionOpen)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                data-testid="email-source-toggle"
              >
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <Mail className="w-3.5 h-3.5 text-amber-500" />
                  Email bron
                </span>
                {emailSectionOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {emailSectionOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
                  <div className="pt-3">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Afzender</span>
                    <span className="text-sm text-slate-700" data-testid="email-source-sender">{entry.source_email}</span>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Onderwerp</span>
                    <span className="text-sm text-slate-700" data-testid="email-source-subject">{entry.source_subject}</span>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Ontvangen</span>
                    <span className="text-sm text-slate-700" data-testid="email-source-date">{formatDate(entry.source_date)}</span>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Email ID</span>
                    <span className="text-[11px] text-slate-500 font-mono break-all" data-testid="email-source-id">{entry.source_email_id}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Document Source + Extracted Data */}
          {entry.extracted_data && (
            <ExtractedDataSection data={entry.extracted_data} />
          )}

          {/* Key Data (contract/document details from AI normalize) */}
          {entry.key_data && (
            <KeyDataSection keyData={entry.key_data} />
          )}

          <div className="bg-white rounded-xl border border-slate-200/80 p-5 space-y-4">
            <h3 className="font-heading text-xs uppercase tracking-wide text-slate-500">Details</h3>

            <div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Status</span>
              <div className="flex items-center gap-2">
                <span className={`inline-block text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_CLASSES[entry.entry_status] || 'badge-active'}`}>
                  {STATUS_LABELS[entry.entry_status] || entry.entry_status}
                </span>
                {(entry.source === 'document_upload' || entry.has_extracted_attachments) && (
                  <span data-testid="detail-document-badge" className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600">
                    <FileTextIcon className="w-2.5 h-2.5" />
                    Document
                  </span>
                )}
              </div>
            </div>

            <div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Locatie</span>
              <span className="flex items-center gap-1.5 text-sm text-slate-700">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                {entry.location_id === 'all' ? 'Alle locaties' : entry.location_id}
              </span>
            </div>

            {entry.bv_id && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Bedrijfsdomein</span>
                <span className="text-sm text-slate-700">{entry.bv_id}</span>
              </div>
            )}

            {entry.category && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Categorie</span>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full cat-${entry.category}`}>
                  {CATEGORIES[entry.category] || entry.category}
                </span>
              </div>
            )}

            {entry.subcategory && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Subcategorie</span>
                <span className="text-sm text-slate-700">{entry.subcategory}</span>
              </div>
            )}

            {entry.tags && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Tags</span>
                <div className="flex flex-wrap gap-1">
                  {entry.tags.split(',').filter(Boolean).map((tag, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-slate-100 space-y-2">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <User className="w-3.5 h-3.5" />
                <span>{entry.created_by}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                <span>Aangemaakt: {formatDate(entry.created_at)}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                <span>Bijgewerkt: {formatDate(entry.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


const DOC_TYPE_LABELS = {
  contract: 'Contract',
  inspection_report: 'Keuringsrapport',
  invoice: 'Factuur',
  permit: 'Vergunning',
  maintenance_report: 'Onderhoudsrapport',
  construction_drawing: 'Bouwtekening',
  other: 'Overig',
};

function ExtractedDataSection({ data }) {
  const [open, setOpen] = React.useState(true);
  if (!data) return null;

  const formatValue = (value) => {
    if (value === null || value === undefined) return '\u2014';
    if (Array.isArray(value)) {
      return value.map(v => {
        if (v && typeof v === 'object') return Object.values(v).join(': ');
        return String(v);
      }).join(', ') || '\u2014';
    }
    if (typeof value === 'object') {
      return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ');
    }
    const s = String(value);
    return (s === 'null' || s === 'undefined' || s === '') ? '\u2014' : s;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="extracted-data-section">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
        data-testid="extracted-data-toggle"
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <Sparkles className="w-3.5 h-3.5 text-[#2E75B6]" />
          Ge{'\u00eb'}xtraheerde data
          {data.document_type && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600">
              {DOC_TYPE_LABELS[data.document_type] || data.document_type}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && data.key_data && (
        <div className="px-4 pb-4 border-t border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
            {Object.entries(data.key_data).filter(([key]) => key !== 'field_sources').map(([key, value]) => (
              <div key={key}>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-0.5">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-xs text-slate-700">
                  {formatValue(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
